import React, { memo, useMemo } from 'react'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'

import Heading from 'components/Luxkit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import { skip, take, without } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getGraphRecommendations } from 'selectors/recomendations/graphSelectors'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import OfferList, { OfferListAction, OfferListAndAction } from 'components/OfferList/OfferList'
import getOfferListKey from 'lib/offer/offerListKey'

const RecommendationsSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string;
  offerId?: string;
  offerType?: App.OfferType;
  filters?: App.OfferListFilters;
  trackingDescription: string;
  useGraph?: boolean;
  propertyId?: string;
  skipFirstNOffers?: number;
  useList?: boolean;
  overrideOfferList?: App.OfferList;
  maxOffers?: number;
  canJoinLereGraphAbTest?: boolean
  // use for overriding the prices and values when having checkIn/checkOut/Occupancy
  enableDateCheckingForPriceDisplay?: boolean
}

const MINIMUM_OFFERS_TO_DISPLAY = 3
const DEFAULT_MAX_OFFERS = 8

const RECOMMENDATION_TYPES: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_LAST_MINUTE,
  'bedbank_hotel',
]

function OfferRecommendations(props: Props) {
  const {
    title = 'You may also like',
    offerId,
    filters = emptyFilters,
    trackingDescription,
    useGraph = false,
    skipFirstNOffers,
    useList = false,
    overrideOfferList,
    maxOffers = DEFAULT_MAX_OFFERS,
    enableDateCheckingForPriceDisplay = false,
  } = props
  const useOverrideList = !!overrideOfferList?.fetching || !!overrideOfferList?.offerIds?.length
  const defaultOfferList = useOfferList({ ...filters, offerTypes: RECOMMENDATION_TYPES }, { disabled: useGraph || useOverrideList })
  const offerList: App.OfferList = useMemo(() => {
    if (useOverrideList) {
      return overrideOfferList
    }
    return defaultOfferList
  }, [defaultOfferList, overrideOfferList, useOverrideList])
  const { offerIds: graphOfferIds, state: graphState } = useAppSelector(state => getGraphRecommendations(state, offerId))

  const selectedDatesAndRoomsFilter: App.OfferListFilters | undefined = useMemo(() => {
    return enableDateCheckingForPriceDisplay ? {
      checkIn: filters.checkIn,
      checkOut: filters.checkOut,
      rooms: filters.rooms,
    } : undefined
  }, [enableDateCheckingForPriceDisplay, filters])

  const offerIds = useMemo(() => {
    let offerCandidates = useGraph ? graphOfferIds : offerList.offerIds

    // remove source offer
    offerCandidates = without(offerCandidates, offerId ?? '')

    // skip first few offers if specified
    if (skipFirstNOffers) {
      offerCandidates = skip(offerCandidates, skipFirstNOffers)
    }

    // take max offers
    offerCandidates = take(offerCandidates, maxOffers)
    return offerCandidates
  }, [offerId, offerList.offerIds, useGraph, graphOfferIds, skipFirstNOffers, maxOffers])

  const algorithm = useGraph ? 'Graph' : (filters.propertyId ? 'Search Nearby' : 'Algolia')
  const tracking = useOfferListTracking(
    `Recommendations-${algorithm}-${trackingDescription}`, {
      ga: {
        category: 'Recommendations',
        label: `${algorithm}-${trackingDescription}`,
      },
      key: getOfferListKey(filters),
    },
  )

  const isIdsFetching = (
    (useGraph && graphState === 'loading') ||
    (!useGraph && offerList.fetching)
  )

  const additionalQueryParams = useMemo(() => buildSearchStringFromFilters(filters), [filters])

  const toShowOfferList = useMemo<App.OfferList>(() => ({
    fetching: isIdsFetching,
    offerIds,
    key: 'offerRecommendations',
  }), [isIdsFetching, offerIds])

  const overrideOfferListAndAction = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: toShowOfferList,
  }), [toShowOfferList])
  const insufficientReco = offerIds.length < MINIMUM_OFFERS_TO_DISPLAY && !isIdsFetching
  if (insufficientReco) {
    return null
  }

  return (
    <OfferListEventsProvider tracking={tracking}>
      <RecommendationsSection>
        <VerticalSpacer gap={16}>
          <LayoutContainer>
            <Heading variant="heading3">{title}</Heading>
          </LayoutContainer>
          {!useList && <OfferListCarousel
            overrideOfferList={toShowOfferList}
            additionalOfferUrlQuery={additionalQueryParams}
            selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
          />}
          {useList && <OfferList
            extraOfferListAndAction={overrideOfferListAndAction}
            tileStyle="search"
          />}
        </VerticalSpacer>
      </RecommendationsSection>
    </OfferListEventsProvider>
  )
}

export default memo(OfferRecommendations)
